import { Spin } from "antd";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";

import { ILogin } from "../../@types/Login";
import { Button } from "../../components/Button";
import { TextField } from "../../components/TextField";
import {
  AUTH_STORED_ROUTE,
  AUTH_UNIT_PREFERENCES,
  IS_MOBILE_APP,
  VIEWED_FIRST_PATIENT,
} from "../../constants/localStorageKeys";
import { useAuth } from "../../hooks/useAuth";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import {
  FooterLinksMobile,
  FooterLinksDesktop,
} from "../../layout/components/FooterLinks";
import { preLoginLogo } from "../../styles/preLoginBranding";
import { isSystemControllerUser } from "../../utils/isSystemControllerUser";
import { mixpanelActions } from "../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../utils/storage";
import { spinCss } from "../Surveys/Answer Survey/Components/Survey/AnswerSurvey.styles";
import {
  Logo,
  Container,
  StyledTile,
  ContentContainer,
  emailTextFieldStyles,
  passwordTextFieldStyles,
  LoadingOverlay,
  loginButtonStyles,
  ErrorMessage,
  InfoMessage,
} from "./LoginPage.styles";

export function LoginPage() {
  const { t } = useTranslation();

  document.title = t("login_documentTitle");

  const [apiFieldErros, setApiFieldErrors] = useState({
    email: "",
    password: "",
  });
  const { authenticate, isAuthenticated, setLoginDetails, setMfaDetails } =
    useAuth();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { changeUnitPreferences } = useUserPreferences();

  const [isLoading, setIsLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [capsLockEnabled, setCapsLockEnabled] = useState(false);
  const [showSetPasswordSuccessMessage, setShowSetPasswordSuccessMessage] =
    useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const isMobileApp = safeStorage.getItem(IS_MOBILE_APP);
      if (!isMobileApp) {
        const storedRoute = safeStorage.getItem(AUTH_STORED_ROUTE);
        safeStorage.removeItem(AUTH_STORED_ROUTE);
        navigate(storedRoute || "/patients");
      }
    }

    const showMessage = Boolean(searchParams.get("showMessage"));
    setShowSetPasswordSuccessMessage(showMessage);

    const unitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);
    if (unitPreferences)
      changeUnitPreferences(safeJsonParse<any>(unitPreferences, {}));
  }, [isAuthenticated]);

  const formValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("login_validation_emailInvalid"))
      .required(t("login_validation_emailRequired")),
    password: yup.string().required(t("login_validation_passwordRequired")),
  });

  const handleOnSubmitForm = async ({ email, password }: ILogin) => {
    setIsLoading(true);

    const responseFromAPI = await authenticate({
      email,
      password,
    });

    const { errors } = responseFromAPI;

    if (errors) {
      setIsLoading(false);
      setGlobalError(errors[0].message);
      return;
    }

    setLoginDetails({
      email,
      password,
    });

    if (
      responseFromAPI.mfaDetails !== null &&
      responseFromAPI.mfaDetails !== undefined
    ) {
      setIsLoading(false);

      responseFromAPI.mfaDetails.email = email;
      setMfaDetails(responseFromAPI.mfaDetails);

      mixpanelActions.track("Visited Screen: MFALoginPage");
      navigate("/mfa");
    } else {
      setIsLoading(false);
      safeStorage.setItem(VIEWED_FIRST_PATIENT, "no");
      const storedRoute = safeStorage.getItem(AUTH_STORED_ROUTE);
      safeStorage.removeItem(AUTH_STORED_ROUTE);
      // EN-4961: SystemController users only have access to the reports
      // dashboard — send them straight there. Role identity rather than the
      // admin-reports:read permission so future roles that also receive the
      // permission (e.g. HealthcareAdmin in Phase 2) keep their default landing.
      if (isSystemControllerUser()) {
        navigate("/reports");
      } else {
        navigate(storedRoute || "/patients");
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  const shouldShowErrorMessage = (field: "email" | "password") => {
    return formik.touched[field]
      ? apiFieldErros[field] || formik.errors[field]
      : "";
  };

  const handleOnKeyUp = (event: any) => {
    const capsEnabled =
      (event.getModifierState && event.getModifierState("CapsLock")) || false;

    setCapsLockEnabled(capsEnabled);

    if (event.key === "Enter") {
      formik.submitForm();
    }
  };

  const handleResetGlobalErrorFieldOnBlur = (field: "email" | "password") => {
    setApiFieldErrors((state: ILogin) => ({ ...state, [field]: "" }));
  };

  const handleOnClickOverForgotPassword = () => {
    mixpanelActions.track("User Action: ClickForgotPassword");
    navigate("/forgot-password");
  };

  const isMobileApp = safeStorage.getItem(IS_MOBILE_APP);
  if (isMobileApp) {
    return <Spin className={spinCss()} fullscreen size="large" />;
  }

  return (
    <Container>
      <StyledTile>
        {isLoading && <LoadingOverlay />}

        <ContentContainer>
          <Logo src={preLoginLogo.src} alt={preLoginLogo.alt} />
          {showSetPasswordSuccessMessage && (
            <InfoMessage>{t("login_passwordResetSuccess")}</InfoMessage>
          )}

          <div>
            <TextField
              name="email"
              label={t("login_emailLabel")}
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder={t("login_emailPlaceholder")}
              className={emailTextFieldStyles()}
              errorMessage={shouldShowErrorMessage("email")}
              onBlur={() => handleResetGlobalErrorFieldOnBlur("email")}
            />

            <TextField
              isPassword
              name="password"
              label={t("login_passwordLabel")}
              placeholder="●●●●●●●●●●●●"
              value={formik.values.password}
              onChange={formik.handleChange}
              className={passwordTextFieldStyles()}
              auxiliarButtonLabel={t("login_forgotPassword")}
              onKeyDown={handleOnKeyUp}
              errorMessage={shouldShowErrorMessage("password")}
              onClickAuxiliarButton={handleOnClickOverForgotPassword}
              onBlur={() => handleResetGlobalErrorFieldOnBlur("password")}
              warningMessage={
                capsLockEnabled ? t("login_capsLockOn") : undefined
              }
            />

            {globalError && <ErrorMessage>{globalError}</ErrorMessage>}
          </div>

          <Button
            type="button"
            label={t("login_submit")}
            isLoading={isLoading}
            className={loginButtonStyles()}
            onClick={formik.submitForm}
          />
        </ContentContainer>
        <FooterLinksMobile />
      </StyledTile>
      <FooterLinksDesktop />
    </Container>
  );
}
